// ======================================================================
// 📁 File: src/modules/ppe/layout/PPESidebar.jsx
// 📝 Purpose:
//   PPE Module Sidebar Navigation
//   - RBAC-driven
//   - Includes "Back to Dashboard"
//   - Domain-wise Masters (Engineering / Material / Resources)
// ======================================================================

import React, { useContext, useEffect, useState } from "react";
import { NavLink, useLocation, Link } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";

// ----------------------------------------------------------------------
// Permission helper
// ----------------------------------------------------------------------
const canAny = (permissions, keys = []) =>
  keys.some((k) => permissions?.[k]?.view === true);

// ----------------------------------------------------------------------
// Sidebar Menu Definition
// ----------------------------------------------------------------------
const MENU = [
  {
    label: "Dashboard",
    route: "/ppe/dashboard",
    perms: ["PPE_VIEW_DASHBOARD"],
    icon: "fas fa-chart-line",
  },
  {
    label: "Orders",
    route: "/ppe/orders",
    perms: ["PPE_VIEW_ORDER", "PPE_CREATE_ORDER", "PPE_EDIT_ORDER"],
    icon: "fas fa-file-alt",
  },

  // ===================== ENGINEERING =====================
  {
    type: "group",
    label: "Engineering",
  },
  {
    label: "Engineering BOQ",
    route: "/ppe/engineering/boq",
    perms: [ "PPE_VIEW_BOQ","PPE_CREATE_BOQ", "PPE_EDIT_BOQ", "PPE_APPROVE_BOQ"],
    icon: "fas fa-project-diagram",
  },
  {
    label: "Engineering Routing",
    route: "/ppe/routing",
    perms: ["PPE_CREATE_ROUTING", "PPE_ASSIGN_ROUTING"],
    icon: "fas fa-random",
  },
  {
    label: "Product Family",
    route: "/ppe/engineering/masters/product-family",
    perms: ["PRODUCT_FAMILY_VIEW"],
    icon: "fas fa-layer-group",
  },
  {
    label: "Product Variant",
    route: "/ppe/engineering/masters/product-variants",
    perms: ["PRODUCT_VARIANT_VIEW"],
    icon: "fas fa-tags",
  },
  {
  label: "Equipment Master",
  route: "/ppe/engineering/masters/equipment",
  perms: ["PRODUCT_VARIANT_VIEW"],
  icon: "fas fa-tools",
},
  {
    label: "Drawing Master",
    route: "/ppe/engineering/masters/drawing",
    perms: ["DRAWING_VIEW"],
    icon: "fas fa-drafting-compass",
  },
  {
    label: "Process Master",
    route: "/ppe/engineering/masters/process",
    perms: ["PROCESS_VIEW"],
    icon: "fas fa-cogs",
  },

  // ===================== MATERIAL =====================
  {
    type: "group",
    label: "Material",
  },
  {
    label: "Raw Material",
    route: "/ppe/material/masters/raw-material",
    perms: ["RAW_MATERIAL_VIEW"],
    icon: "fas fa-cube",
  },
  {
    label: "Bought-Out Item",
    route: "/ppe/material/masters/boughtout",
    perms: ["BOUGHT_OUT_ITEM_VIEW", "PPE_BOUGHTOUT_ITEM"],
    icon: "fas fa-boxes",
  },

  // ===================== UPLOADS =====================
{
    type: "group",
    label: "Uploads",
},
{
    label: "Generic Upload",
    route: "/ppe/uploads",
    perms: ["RAW_MATERIAL_VIEW"],   // Temporary
    icon: "fas fa-file-upload",
},
  
  // ===================== PROCUREMENT =====================
{
  type: "group",
  label: "Procurement",
},
{
  label: "Procurement Dashboard",
  route: "/ppe/procurement",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-chart-bar",
},

{
  label: "Indent",
  route: "/ppe/procurement/indent",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-indent",
},
{
  label: "Purchase Request",
  route: "/ppe/procurement/purchase-request",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-clipboard-list",
},
{
  label: "RFQ",
  route: "/ppe/procurement/rfq",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-envelope-open-text",
},
{
  label: "RFQ Quote Entry",
  route: "/ppe/procurement/rfq/quote-entry",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-file-signature",
},
{
  label: "Purchase Order",
  route: "/ppe/procurement/purchase-order",
  perms: ["PPE_PO_VIEW"],
  icon: "fas fa-file-invoice",
},
{
  label: "My Approvals",
  route: "/ppe/procurement/my-approvals",
  perms: ["PPE_PO_VIEW"],
  icon: "fas fa-check-circle",
},
{
  label: "GRN",
  route: "/ppe/procurement/grn",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-dolly-flatbed",
},
{
  label: "Purchase Return",
  route: "/ppe/procurement/purchase-return",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-undo-alt",
},

{
  label: "Purchase Invoice",
  route: "/ppe/procurement/purchase-invoice",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-file-invoice-dollar",
},
// ===================== QUALITY =====================
{
  type: "group",
  label: "Quality",
},

{
  label: "Incoming Inspection",
  route: "/ppe/quality/incoming",
  perms: [
    "PPE_GRN_VIEW",
    "PPE_INDENT_VIEW"
  ],
  icon: "fas fa-clipboard-check",
},

// ===================== TRANSPORT =====================
{
  type: "group",
  label: "Transport",
},

{
  label: "Transport Dashboard",
  route: "/ppe/transport/dashboard",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-truck-moving",
},

{
  label: "Transport Requests",
  route: "/ppe/transport/list",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-shipping-fast",
},


// ===================== INVENTORY =====================
// ===================== INVENTORY =====================
{
  type: "group",
  label: "Inventory",
},

// ---------- OPERATIONS ----------
{
  label: "Stock Ledger",
  route: "/ppe/inventory/ledger",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-book",
},
{
  label: "Warehouse Transfer",
  route: "/ppe/inventory/transfer",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-exchange-alt",
},

// ---------- REPORTS ----------
{
  type: "group",
  label: "Inventory Reports",
},
{
  label: "Current Stock",
  route: "/ppe/inventory/reports/current-stock",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-box-open",
},
{
  label: "Inventory Valuation",
  route: "/ppe/inventory/reports/valuation",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-coins",
},
{
  label: "Stock Register",
  route: "/ppe/inventory/reports/stock-register",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-list",
},
{
  label: "Inventory Ledger",
  route: "/ppe/inventory/reports/inventory-ledger",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-stream",
},
{
  label: "Item Ledger",
  route: "/ppe/inventory/reports/item-ledger",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-history",
},

{
  label: "Raw Material Master Report",
  route: "/ppe/inventory/reports/raw-material-master",
  perms: ["PPE_GRN_VIEW"],
  icon: "fas fa-database",
},


// ===================== COSTING =====================
{
  type: "group",
  label: "Costing",
},
{
  label: "Material Costing",
  route: "/ppe/costing/material",
  perms: ["PPE_COSTING_VIEW"],
  icon: "fas fa-coins",
},

// ===================== FOLLOWUP MANAGEMENT =====================
{
  type: "group",
  label: "Followups",
},
{
  label: "Followup Management",
  route: "/ppe/fms/followups",
  perms: ["PPE_INDENT_VIEW"],
  icon: "fas fa-tasks",
},

// --------------------- AUTOMATION RULES ------------------------
{
  label: "Automation Rules",
  route: "/ppe/fms/rules",
  perms: ["PPE_INDENT_VIEW"], // adjust later if you create rule permission
  icon: "fas fa-robot",
},

{
  type: "group",
  label: "Production",
},

// ===================== PRODUCTION =====================
  {
    label: "Production Planning",
    route: "/ppe/planning",
    perms: [
      "PPE_CREATE_PLAN",
      "PPE_EDIT_PLAN",
      "PPE_RELEASE_PLAN",
      "PPE_VIEW_PLAN",
      "PPE_REPLAN",
    ],
    icon: "fas fa-calendar-alt",
  },
  {
  label: "Production Execution",
  route: "/ppe/execution",
  perms: ["PPE_RELEASE_PLAN"],
  icon: "fas fa-play-circle",
},
// ADDED 2026-02-23 — Store Execution
{
  label: "Material Issue",
  route: "/ppe/execution/material-issue",
  perms: ["PPE_RELEASE_PLAN"], // reuse execution permission
  icon: "fas fa-box-open",
},

{
    label: "Shop Floor (MES)",
    route: "/ppe/mes",
    perms: [
      "PPE_START_ACTIVITY",
      "PPE_HOLD_ACTIVITY",
      "PPE_RESUME_ACTIVITY",
      "PPE_COMPLETE_ACTIVITY",
      "PPE_EXECUTION",
    ],
    icon: "fas fa-industry",
  },

  // ===================== RESOURCES =====================
  {
    type: "group",
    label: "Resources",
  },
  {
    label: "Machine Group",
    route: "/ppe/resources/masters/machine-group",
    perms: ["MACHINE_GROUP_VIEW"],
    icon: "fas fa-object-group",
  },
  {
    label: "Machine",
    route: "/ppe/resources/masters/machine",
    perms: ["MACHINE_VIEW"],
    icon: "fas fa-industry",
  },
  {
    label: "Tool",
    route: "/ppe/resources/masters/tool",
    perms: ["TOOL_VIEW"],
    icon: "fas fa-tools",
  },
  {
    label: "Skill",
    route: "/ppe/resources/masters/skill",
    perms: ["SKILL_VIEW"],
    icon: "fas fa-user-cog",
  },
  {
    label: "Worker",
    route: "/ppe/resources/masters/worker",
    perms: ["WORKER_VIEW"],
    icon: "fas fa-users",
  },

  // ===================== MASTERS =====================
{
  type: "group",
  label: "Masters",
},
{
  label: "Business Partner",
  route: "/ppe/masters/business-partner",
  perms: ["BP_VIEW"],
  icon: "fas fa-handshake",
},
{
  label: "SKU Attributes",
  route: "/ppe/masters/sku-attributes",
  perms: ["BP_VIEW"], // IMPORTANT
  icon: "fas fa-cogs",
},
  // ===================== EXECUTION =====================
  
  
  {
    label: "Dispatch",
    route: "/ppe/dispatch",
    perms: ["PPE_DISPATCH", "PPE_CLOSE_ORDER"],
    icon: "fas fa-truck",
  },
  {
    type: "group",
    label: "Reports",
  },
  {
    label: "Material Lifecycle",
    route: "/ppe/reports/lifecycle",
    perms: ["PPE_VIEW_REPORTS"],
    icon: "fas fa-project-diagram",
  },
   {
    label: "Procurement Lifecycle",
    route: "/ppe/reports/procurement-lifecycle",
    perms: ["PPE_VIEW_REPORTS"],
    icon: "fas fa-file-invoice",
  },
  {
    label: "Reports",
    route: "/ppe/reports",
    perms: ["PPE_VIEW_REPORTS"],
    icon: "fas fa-chart-pie",
  },
];

// ======================================================================
// PPE Sidebar Component
// ======================================================================
export default function PPESidebar({ onToggle }) {
  const { permissions } = useContext(AuthContext);
  const location = useLocation();

  const collapsedLS = localStorage.getItem("ppe_sidebar_collapsed");
  const [expanded, setExpanded] = useState(
    collapsedLS === null ? true : collapsedLS === "false"
  );

  useEffect(() => {
    localStorage.setItem(
      "ppe_sidebar_collapsed",
      expanded ? "false" : "true"
    );
    onToggle?.(expanded);
  }, [expanded, onToggle]);

  return (
    <aside
      style={{
        width: expanded ? "260px" : "64px",
        transition: "width 0.22s ease",
        background: "#1f2937",
        color: "#fff",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        zIndex: 100,
      }}
    >
      {/* Header */}
      <div
        style={{
          height: "56px",
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          padding: expanded ? "0 12px" : "0",
          borderBottom: "1px solid #374151",
        }}
      >
        {expanded && <span style={{ fontWeight: 700 }}>PPE</span>}
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
          }}
        >
          <i className="fas fa-bars" />
        </button>
      </div>

      {/* Back to Dashboard */}
      <Link
        to="/dashboard"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: 12,
          padding: "10px 12px",
          borderRadius: 999,
          background: "#4f46e5",
          color: "#fff",
          textDecoration: "none",
          justifyContent: expanded ? "flex-start" : "center",
        }}
      >
        <i className="fas fa-home" />
        {expanded && <span>Back to Dashboard</span>}
      </Link>

      {/* Menu */}
      <nav
        style={{
          maxHeight: "calc(100vh - 140px)",
          overflowY: "auto",
        }}
      >
        {MENU.map((item, idx) => {
          if (item.type === "group") {
            if (!expanded) return null;
            return (
              <div
                key={`grp-${idx}`}
                style={{
                  margin: "12px 14px 6px",
                  fontSize: 11,
                  color: "#9ca3af",
                  textTransform: "uppercase",
                }}
              >
                {item.label}
              </div>
            );
          }

          if (!canAny(permissions, item.perms)) return null;

          const active = location.pathname.startsWith(item.route);

          return (
            <NavLink
              key={item.route}
              to={item.route}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: expanded ? "10px 14px" : "10px 0",
                justifyContent: expanded ? "flex-start" : "center",
                color: active ? "#fff" : "#cbd5f5",
                background: active ? "#374151" : "transparent",
                textDecoration: "none",
                fontSize: 14,
              }}
            >
              <i className={item.icon} />
              {expanded && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
